// src/pages/profiles/profile-detail-page/profile-detail/EducationCareerTab.jsx
import React from "react";
import {
  Grid, Box, Typography, Chip, Stack, Paper, Avatar, useTheme, useMediaQuery
} from "@mui/material";
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import HomeIcon from '@mui/icons-material/Home';
import BusinessIcon from '@mui/icons-material/Business';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ApartmentIcon from '@mui/icons-material/Apartment';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CardTravelIcon from '@mui/icons-material/CardTravel';
import PublicIcon from '@mui/icons-material/Public';
import InfoCard from "./components/InfoCard";

// Helper function (can be moved to utils)
const formatIncome = (income) => {
  if (!income) return null;
  if (typeof income === 'number') {
    if (income >= 100000) return `PKR ${(income / 100000).toFixed(1)} Lakh/mo`;
    if (income >= 1000) return `PKR ${Math.round(income / 1000)}K/mo`;
    return `PKR ${income}/mo`;
  }
  return income;
};

const IconBox = ({ icon }) => (
  <Box sx={{
    width: 40, height: 40, borderRadius: 2,
    bgcolor: 'primary.alpha20',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  }}>
    {React.cloneElement(icon, { sx: { color: 'primary.light' } })}
  </Box>
);

const SmallIconBox = ({ icon }) => (
  <Box sx={{
    width: 24, height: 24, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    bgcolor: 'primary.alpha20', mb: 1, mx: 'auto'
  }}>
    {icon}
  </Box>
);

const InfoRow = ({ icon, label, value }) => (
  <Stack direction="row" spacing={2} alignItems="center" sx={{
    p: 2, borderRadius: 2, bgcolor: 'primary.alpha10',
    border: 1, borderColor: 'divider', mb: 1.5
  }}>
    <IconBox icon={icon} />
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>{label}</Typography>
      <Typography variant="body1" color="text.primary" sx={{ fontWeight: 600 }}>{value || '-'}</Typography>
    </Box>
  </Stack>
);

const Section = ({ title, icon, children, ...props }) => (
  <InfoCard title={title} icon={icon} sx={{
    bgcolor: 'background.default',
    border: 1, borderColor: 'divider',
    height: '100%',
    ...props
  }}>
    {children}
  </InfoCard>
);

const EducationCareerTab = ({ profile }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const education = profile?.education || {};
  const profession = profile?.profession || {};
  const residence = profile?.residence || {};

  return (
    <Grid container spacing={isMobile ? 2 : 3}>

      {/* Education */}
      <Grid item xs={12} md={6}>
        <Section title="Education" icon={<SchoolIcon />}>
          {[
            { icon: <MenuBookIcon />, label: "Highest Degree", value: education?.highest_degree || profile?.highest_degree },
            { icon: <ApartmentIcon />, label: "Institution", value: education?.institution || profile?.institution }
          ].map((item, i) => <InfoRow key={i} {...item} />)}

          {(education?.other_qualifications?.length > 0 || profile?.other_qualifications?.length > 0) && (
            <Paper sx={{
              p: 2, borderRadius: 2,
              bgcolor: 'primary.alpha10',
              border: 1, borderColor: 'divider',
              mt: 1
            }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 1, display: 'block' }}>
                Other Qualifications
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {(education?.other_qualifications || profile?.other_qualifications || []).map((qual, i) => (
                  <Chip key={i} label={qual} size="small" sx={{
                    bgcolor: 'primary.alpha20',
                    color: 'primary.light',
                    fontWeight: 500,
                    border: 1, borderColor: 'divider'
                  }} />
                ))}
              </Box>
            </Paper>
          )}
        </Section>
      </Grid>

      {/* Profession */}
      <Grid item xs={12} md={6}>
        <Section title="Profession" icon={<WorkIcon />}>
          {[
            { icon: <BusinessIcon />, label: "Job Title", value: profession?.job_title || profile?.job_title },
            { icon: <ApartmentIcon />, label: "Organization", value: profession?.organization || profile?.organization },
            { icon: <AssignmentIcon />, label: "Employment Type", value: profession?.employment_type || profile?.employment_type },
            { icon: <AttachMoneyIcon />, label: "Monthly Income", value: formatIncome(profession?.monthly_income || profile?.monthly_income) },
            { icon: <LocationOnIcon />, label: "Work Location", value: profession?.work_location || profile?.work_location }
          ].map((item, i) => <InfoRow key={i} {...item} />)}
        </Section>
      </Grid>

      {/* Residence */}
      <Grid item xs={12}>
        <Section title="Residence" icon={<HomeIcon />} sx={{ minHeight: 320 }}>
          <Box sx={{ p: 1.5 }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {[
                { icon: <CardTravelIcon />, label: "Status", value: residence?.residential_status || profile?.residential_status },
                { icon: <PublicIcon />, label: "Country", value: residence?.country || profile?.country },
                { icon: <PublicIcon />, label: "State", value: residence?.state || profile?.state },
                { icon: <LocationOnIcon />, label: "City", value: residence?.city || profile?.city }
              ].map((item, i) => (
                <Grid item xs={6} md={3} key={i}>
                  <Paper sx={{
                    p: 2, borderRadius: 2,
                    bgcolor: 'primary.alpha10',
                    border: 1, borderColor: 'divider',
                    textAlign: 'center'
                  }}>
                    <SmallIconBox icon={item.icon} />
                    <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                    <Typography variant="body2" fontWeight={700} color="text.primary">
                      {item.value || '-'}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {(residence?.permanent_address || profile?.permanent_address) && (
              <Paper sx={{
                p: 2, borderRadius: 2,
                bgcolor: 'primary.alpha10',
                border: 1, borderColor: 'divider'
              }}>
                <Stack direction="row" spacing={2}>
                  <IconBox icon={<LocationOnIcon />} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Permanent Address</Typography>
                    <Typography variant="body1" fontWeight={600} color="text.primary">
                      {residence?.permanent_address || profile?.permanent_address}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            )}
          </Box>
        </Section>
      </Grid>

      {/* Summary */}
      <Grid item xs={12}>
        <Paper sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          bgcolor: 'primary.alpha10',
          border: 1, borderColor: 'divider'
        }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'primary.alpha20', color: 'primary.light', mr: 2 }}>
                  <SchoolIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={700} color="text.primary">
                    Education & Career
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Summary
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                justifyContent: { xs: 'center', md: 'flex-end' }
              }}>
                {[
                  { icon: <SchoolIcon />, label: education?.highest_degree || profile?.highest_degree },
                  { icon: <WorkIcon />, label: profession?.job_title || profile?.job_title },
                  { icon: <AttachMoneyIcon />, label: formatIncome(profession?.monthly_income || profile?.monthly_income) },
                  { icon: <LocationOnIcon />, label: residence?.city || profile?.city }
                ].filter(i => i.label).map((item, i) => (
                  <Chip
                    key={i}
                    label={item.label}
                    icon={item.icon}
                    size="small"
                    sx={{
                      bgcolor: 'primary.alpha20',
                      color: 'primary.light',
                      fontWeight: 600,
                      border: 1,
                      borderColor: 'divider'
                    }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default EducationCareerTab;